import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Home, Shield, Award, MapPin, Clock, Car, Train } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Layout from "@/components/Layout";
import PropertyCard from "@/components/PropertyCard";
import PropertyMap from "@/components/PropertyMap";
import PastProjectsGallery from "@/components/PastProjectsGallery";
import { useProperties } from "@/hooks/useProperties";
import ContactPopover from "@/components/ContactPopover";
import { formatPrice } from "@/data/properties";
import heroImage from "@/assets/hero-interior.jpeg";
import ctaBackground from "@/assets/cta-background.jpeg";
import WhatsAppIcon from "@/components/WhatsAppIcon";

// Placeholder images for past projects — replace with real photos when provided
const pastProjects = [
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=80",
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=900&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900&q=80",
];

export default function Index() {
  const { available, loading } = useProperties();

  return (
    <Layout>
      <Helmet>
        <title>Viviendas de obra nueva en el Vallès Occidental | Sercom</title>
        <meta name="description" content="Casas y villas de obra nueva en el Vallès Occidental y provincia de Barcelona. Construcción propia, a 30 minutos de la ciudad. Descubre las propiedades disponibles." />
        <link rel="canonical" href="https://sercomcs.com/" />
        <meta property="og:title" content="Viviendas de obra nueva en el Vallès Occidental | Sercom" />
        <meta property="og:description" content="Casas y villas de obra nueva en el Vallès Occidental y provincia de Barcelona. A 30 minutos de la ciudad." />
        <meta property="og:url" content="https://sercomcs.com/" />
      </Helmet>
      <h1 className="sr-only">Viviendas de obra nueva en el Vallès Occidental — Sercom Inmobiliaria</h1>
      {/* HERO */}
      <section className="relative h-screen overflow-hidden">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 8, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={heroImage}
            alt="Interior de vivienda Sercom"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-20 md:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-accent text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
          >
            Vallès Occidental · Barcelona
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] mb-6"
            aria-hidden="true"
          >
            Tu próximo
            <br />
            <span className="text-gradient-amber">hogar</span>
            <br />
            te espera.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-white/80 text-lg md:text-xl max-w-xl mb-8"
          >
            Viviendas en el Vallès Occidental, a 30 minutos de Barcelona.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/Propiedades"
              className="inline-flex items-center gap-2 bg-white text-sercom-black px-8 py-3.5 rounded-full font-semibold hover:bg-white/90 transition-colors"
            >
              Ver propiedades
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/Contacto"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-3.5 font-semibold text-white backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-white/60 hover:bg-white/20"
            >
              Contactar
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-white/80" />
        </motion.div>
      </section>

      {/* PROPIEDADES DISPONIBLES */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-accent text-sm font-semibold tracking-wider uppercase">Selección actual</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2">Propiedades disponibles</h2>
            </div>
            <Link
              to="/Propiedades"
              aria-label="Ver todas las propiedades disponibles"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Ver todas las propiedades <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading &&
              [0, 1, 2].map((i) => (
                <div key={i} className="apple-shadow overflow-hidden rounded-lg border bg-card/90">
                  <div className="aspect-[4/3] animate-pulse bg-muted" />
                  <div className="space-y-3 p-5">
                    <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                    <div className="h-10 w-full animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            {!loading &&
              available.map((p, i) => (
                <PropertyCard key={p.id} property={p} index={i} />
              ))}
          </div>
          {!loading && available.length === 0 && (
            <p className="text-center text-muted-foreground">
              Ahora mismo no hay propiedades publicadas. Escríbenos y te avisamos de las próximas.
            </p>
          )}
          <div className="mt-10 text-center sm:hidden">
            <Link to="/Propiedades" className="inline-flex items-center gap-1 text-sm font-medium text-accent">
              Ver todas las propiedades <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* UBICACIÓN */}
      <section className="py-20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-accent text-sm font-semibold tracking-wider uppercase">Localización</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Nuestro territorio,{" "}
                <span className="text-gradient-gold">tu próximo hogar</span>
              </h2>
              <ul className="space-y-4 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-0.5 shrink-0" style={{ stroke: "url(#goldGrad)" }} />
                  <span>Trabajamos en todo el Vallès Occidental y la provincia de Barcelona: Terrassa, Sabadell, Sant Cugat, Rubí, Viladecavalls y alrededores</span>
                </li>
                <li className="flex items-start gap-3">
                  <Car className="w-5 h-5 mt-0.5 shrink-0" style={{ stroke: "url(#goldGrad)" }} />
                  <span>A 30 minutos en coche de Barcelona por las autopistas C-58, C-16 y AP-7</span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 mt-0.5 shrink-0" style={{ stroke: "url(#goldGrad)" }} />
                  <span>Municipios con todos los servicios: comercios, colegios, hospitales, transporte y ocio</span>
                </li>
                <li className="flex items-start gap-3">
                  <Home className="w-5 h-5 mt-0.5 shrink-0" style={{ stroke: "url(#goldGrad)" }} />
                  <span>Entorno natural privilegiado, junto al Parc Natural de Sant Llorenç del Munt i l'Obac</span>
                </li>
              </ul>
              {/* SVG gradient definition for lucide icons */}
              <svg width="0" height="0" className="absolute">
                <defs>
                  <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fde68a" />
                    <stop offset="50%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#b45309" />
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <PropertyMap
                locations={[
                  { address: "Terrassa", title: "Terrassa" },
                  { address: "Sabadell", title: "Sabadell" },
                  { address: "Sant Cugat", title: "Sant Cugat del Vallès" },
                  { address: "Rubí", title: "Rubí" },
                  { address: "Viladecavalls", title: "Viladecavalls" },
                ]}
                className="shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* POR QUÉ SERCOM */}
      <section className="py-24 bg-sercom-black relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 divider-gold opacity-60" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-accent text-xs font-semibold tracking-[0.4em] uppercase">Nuestra diferencia</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 leading-tight">
              ¿Por qué <span className="text-gradient-gold italic">Sercom</span>?
            </h2>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Línea dorada vertical central (solo desktop) */}
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-accent/40 to-transparent hidden md:block" aria-hidden="true" />

            <div className="space-y-16 md:space-y-24">
              {[
                { icon: Home, title: "Acabados de calidad", desc: "Trabajamos con materiales de calidad y cuidamos cada detalle del acabado en todas nuestras viviendas." },
                { icon: Shield, title: "Clientes satisfechos", desc: "Las familias que viven en nuestras viviendas son nuestra mejor referencia." },
                { icon: Award, title: "Experiencia consolidada", desc: "Años de experiencia en el Vallès Occidental nos dan un conocimiento sólido de la zona, la normativa y el mercado." },
              ].map((item, i) => {
                const isRight = i % 2 === 1;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: isRight ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.6 }}
                    className="grid md:grid-cols-2 gap-8 items-center relative"
                  >
                    {/* Punto en la línea central (desktop) */}
                    <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gradient-to-br from-amber-200 to-amber-700 ring-4 ring-sercom-black z-10" aria-hidden="true" />

                    {!isRight ? (
                      <>
                        <div className="md:text-right md:pr-12">
                          <div className="inline-flex items-center gap-3 mb-4 md:flex-row-reverse">
                            <span className="text-gradient-gold text-sm tracking-[0.3em] font-medium">0{i + 1}</span>
                            <div className="w-10 h-px bg-accent/60" />
                            <div className="w-10 h-10 rounded-full border border-accent/40 flex items-center justify-center">
                              <item.icon className="w-5 h-5 text-accent" />
                            </div>
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{item.title}</h3>
                          <p className="text-white/80 leading-relaxed md:ml-auto md:max-w-md">{item.desc}</p>
                        </div>
                        <div />
                      </>
                    ) : (
                      <>
                        <div />
                        <div className="md:pl-12">
                          <div className="inline-flex items-center gap-3 mb-4">
                            <span className="text-gradient-gold text-sm tracking-[0.3em] font-medium">0{i + 1}</span>
                            <div className="w-10 h-px bg-accent/60" />
                            <div className="w-10 h-10 rounded-full border border-accent/40 flex items-center justify-center">
                              <item.icon className="w-5 h-5 text-accent" />
                            </div>
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{item.title}</h3>
                          <p className="text-white/80 leading-relaxed md:max-w-md">{item.desc}</p>
                        </div>
                      </>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Proyectos anteriores */}
          <div className="mt-24">
            <div className="text-center mb-10">
              <span className="text-accent text-xs font-semibold tracking-[0.4em] uppercase">Nuestro trabajo</span>
              <h3 className="text-2xl md:text-3xl font-bold text-white mt-3">Casas que hemos construido</h3>
            </div>
            <PastProjectsGallery />
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative py-28 overflow-hidden">
        <img
          src={ctaBackground}
          alt="Casa Sercom con piscina y jardín"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl font-bold text-white mb-6"
          >
            Encuentra tu <span className="text-gradient-amber">hogar ideal</span> en el Vallès Occidental
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-white/80 text-lg mb-10 max-w-2xl mx-auto"
          >
            Habla con nuestro equipo y descubre las oportunidades que tenemos para ti.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="inline-block"
          >
            <ContactPopover
              whatsappText="Hola, quiero información sobre propiedades"
              trigger={
                  <button className="glass-surface inline-flex items-center gap-3 rounded-full bg-accent/90 px-10 py-4 text-lg font-bold text-accent-foreground transition-all hover:-translate-y-1 hover:shadow-2xl">
                   <WhatsAppIcon className="h-8 w-8" />
                   Habla con nosotros
                </button>
              }
            />
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
